'use client'

import { useEffect, useState } from 'react'
import { Sidebar, MobileMenuButton, type SidebarMenuItem, type SidebarBranding } from './Sidebar'

export interface AppLayoutProps {
  children: React.ReactNode
  
  // Sidebar configuration
  branding: SidebarBranding
  menuItems: SidebarMenuItem[]
  currentPath: string
  onNavigate: (path: string) => void
  
  // Auth
  isAuthenticated?: boolean
  onLogin?: () => void
  onLogout?: () => void
  
  // Optional components
  topBarContent?: React.ReactNode
  sidebarFooter?: React.ReactNode
  
  // Loading state
  isLoading?: boolean
  loadingComponent?: React.ReactNode
}

export function AppLayout({ 
  children,
  branding,
  menuItems,
  currentPath,
  onNavigate,
  isAuthenticated = false,
  onLogin,
  onLogout,
  topBarContent,
  sidebarFooter,
  isLoading = false,
  loadingComponent
}: AppLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)

  // Auto-collapse the desktop sidebar on narrow viewports (laptops between the
  // `md` and `xl` breakpoints) so the main content keeps enough room. The user
  // can still toggle manually; crossing the breakpoint re-applies the default.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const narrowDesktop = window.matchMedia('(min-width: 768px) and (max-width: 1279px)')
    const apply = (matches: boolean) => setSidebarCollapsed(matches)
    apply(narrowDesktop.matches)
    const handleChange = (event: MediaQueryListEvent) => apply(event.matches)
    narrowDesktop.addEventListener('change', handleChange)
    return () => narrowDesktop.removeEventListener('change', handleChange)
  }, [])

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen)
  }

  const toggleSidebarCollapse = () => {
    setSidebarCollapsed(!sidebarCollapsed)
  }

  // Show loading while checking authentication or other async operations
  if (isLoading) {
    return (
      loadingComponent || (
        <div className="min-h-dvh bg-background-primary flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-brand-primary border-t-transparent rounded-full animate-spin" />
        </div>
      )
    )
  }

  return (
    <div className="h-dvh overflow-hidden bg-background-secondary flex">
      {/* Sidebar - sempre visível no desktop */}
      <div className={`hidden md:block flex-shrink-0 transition-all duration-300 ${sidebarCollapsed ? 'md:w-[80px]' : 'md:w-[280px]'}`}>
        <Sidebar 
          isOpen={true} 
          onToggle={toggleSidebar} 
          showCloseButton={false}
          branding={branding}
          menuItems={menuItems}
          currentPath={currentPath}
          onNavigate={onNavigate}
          isAuthenticated={isAuthenticated}
          onLogin={onLogin}
          onLogout={onLogout}
          footer={sidebarFooter}
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={toggleSidebarCollapse}
        />
      </div>
      
      {/* Sidebar mobile */}
      <div className="md:hidden">
        <Sidebar 
          isOpen={sidebarOpen} 
          onToggle={toggleSidebar} 
          showCloseButton={true}
          branding={branding}
          menuItems={menuItems}
          currentPath={currentPath}
          onNavigate={onNavigate}
          isAuthenticated={isAuthenticated}
          onLogin={onLogin}
          onLogout={onLogout}
          footer={sidebarFooter}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile header - sempre visível no mobile */}
        <div className="sticky top-0 z-40 bg-background-white border-b border-gray-200/20 px-3 py-2.5 flex items-center justify-between md:hidden">
          {/* Mobile menu button */}
          <MobileMenuButton onToggle={toggleSidebar} />

          {/* Mobile logo */}
          <div className="flex items-center space-x-2">
            {branding.logoComponent ? (
              <div className="scale-75">{branding.logoComponent}</div>
            ) : (
              <>
                <div className="w-7 h-7 bg-gradient-to-br from-brand-primary to-brand-secondary rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-xs">
                    {branding.logoIcon || branding.logoText.charAt(0).toUpperCase()}
                  </span>
                </div>
                <span className="font-semibold text-sm text-text-primary">{branding.appName}</span>
              </>
            )}
          </div>

          {/* Spacer para centralizar o logo */}
          <div className="w-8" />
        </div>

        {/* Desktop top bar */}
        {topBarContent && (
          <div className="hidden md:flex sticky top-0 z-40 bg-background-white border-b border-gray-200/20 p-4 items-center justify-between">
            <div className="flex items-center space-x-4">
              {topBarContent}
            </div>
          </div>
        )}

        {/* Page Content */}
        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  )
}